import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumAlcoholType } from "./enums/enumAlcoholType.generated";
import { EnumAssemblyStatus } from "./enums/enumAssemblyStatus.generated";
import { EnumAssemblyType } from "./enums/enumAssemblyType.generated";
import { EnumDocumentFileType } from "./enums/enumDocumentFileType.generated";
import { EnumProcessIngredientSource } from "./enums/enumProcessIngredientSource.generated";
import { EnumProcessOutputTarget } from "./enums/enumProcessOutputTarget.generated";
import { EnumYesNoEither } from "./enums/enumYesNoEither.generated";

// Model Objects

export class BatchOutput extends ModelObject<BatchOutput> {

    public quantity: number = 0;
    public stockItemId: Guid = Guid.createEmpty();
    public unitOfMeasureId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.quantity = jsonSerializedObject.quantity;
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.quantity = this.quantity;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "BatchOutput.StockItemId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "BatchOutput.UnitOfMeasureId cannot be empty"},
    ];
}


export class BatchHeaderReadOnly extends ModelObject<BatchHeaderReadOnly> {

    private _startedAt: Instant | null = null;
    /**
    * Timestamp at which the batch was started.
    */
    public get startedAt(): Instant | null { return this._startedAt }
    public set startedAt(value: Instant | null) {
      this._startedAt = value === undefined ? null : value;
    }
    private _startedBy: Guid | null = null;
    /**
    * User who has started the batch.
    */
    public get startedBy(): Guid | null { return this._startedBy }
    public set startedBy(value: Guid | null) {
      this._startedBy = value === undefined ? null : value;
    }
    private _completedAt: Instant | null = null;
    /**
    * Timestamp at which the batch was completed.
    */
    public get completedAt(): Instant | null { return this._completedAt }
    public set completedAt(value: Instant | null) {
      this._completedAt = value === undefined ? null : value;
    }
    private _cancelledAt: Instant | null = null;
    /**
    * Timestamp at which the assembly was cancelled.
    */
    public get cancelledAt(): Instant | null { return this._cancelledAt }
    public set cancelledAt(value: Instant | null) {
      this._cancelledAt = value === undefined ? null : value;
    }
    /**
    * True if this batch has been previously completed, and then un-completed.
    */
    public hasBeenPreviouslyCompleted: boolean = false;
    /**
    * Total Litres of all ingredients already used in batch. (Will be 0 if not yet started)
    */
    public totalIngredientUsedLitres: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.startedAt = jsonSerializedObject.startedAt == null ? null : Instant.parse(jsonSerializedObject.startedAt);
          this.startedBy = jsonSerializedObject.startedBy == null ? null : Guid.parseOrEmpty(jsonSerializedObject.startedBy);
          this.completedAt = jsonSerializedObject.completedAt == null ? null : Instant.parse(jsonSerializedObject.completedAt);
          this.cancelledAt = jsonSerializedObject.cancelledAt == null ? null : Instant.parse(jsonSerializedObject.cancelledAt);
          this.hasBeenPreviouslyCompleted = jsonSerializedObject.hasBeenPreviouslyCompleted;
          this.totalIngredientUsedLitres = jsonSerializedObject.totalIngredientUsedLitres;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.startedAt = this.startedAt == null ? null : this.startedAt.toString();
        toRet.startedBy = this.startedBy == null ? null : this.startedBy.toString();
        toRet.completedAt = this.completedAt == null ? null : this.completedAt.toString();
        toRet.cancelledAt = this.cancelledAt == null ? null : this.cancelledAt.toString();
        toRet.hasBeenPreviouslyCompleted = this.hasBeenPreviouslyCompleted;
        toRet.totalIngredientUsedLitres = this.totalIngredientUsedLitres;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "startedBy", rule: (v: any) => v == null || !v.isEmpty() || "BatchHeaderReadOnly.StartedBy cannot be empty"},
    ];
}


export class BatchHeaderCalculated extends ModelObject<BatchHeaderCalculated> {

    public status: EnumAssemblyStatus = EnumAssemblyStatus.open;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.status = EnumAssemblyStatus.getByValue(jsonSerializedObject.status, this.status);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.status = this.status.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class BatchHeader extends ModelObject<BatchHeader> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    public isHidden: boolean = false;
    private _materialProducedStockItemId: Guid | null = null;
    /**
    * Stock Item Id produced by the batch
    */
    public get materialProducedStockItemId(): Guid | null { return this._materialProducedStockItemId }
    public set materialProducedStockItemId(value: Guid | null) {
      this._materialProducedStockItemId = value === undefined ? null : value;
    }
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Stock Location Id
    */
    public stockLocationId: Guid = Guid.createEmpty();
    /**
    * Urn. Will be either allocated at begin batch, or will be provided by user, or left blank.
    * Not Nullable
    * Max Length : 1000
    */
    public urn: string = "";
    /**
    * When True, Urn will be system allocated at begin batch, When false, Urn must be provided by user if needed.
    */
    public autoAllocateUrnAtStart: boolean = false;
    /**
    * Vessel Fill Colour
    * Not Nullable
    */
    public vesselFillColour: string = "";
    /**
    * Batch Start Date. Either planned (if batch not started) or actual (if started). Editable by the user.
    */
    public startDate: LocalDate = LocalDate.ofEpochDay(0);
    private _batchTemplateId: Guid | null = null;
    /**
    * Batch template from which this batch was initially created (if any). For reference only.
    */
    public get batchTemplateId(): Guid | null { return this._batchTemplateId }
    public set batchTemplateId(value: Guid | null) {
      this._batchTemplateId = value === undefined ? null : value;
    }
    public readOnly: BatchHeaderReadOnly = new BatchHeaderReadOnly();
    public calculated: BatchHeaderCalculated = new BatchHeaderCalculated();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.isHidden = jsonSerializedObject.isHidden;
          this.materialProducedStockItemId = jsonSerializedObject.materialProducedStockItemId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.materialProducedStockItemId);
          this.notes = jsonSerializedObject.notes;
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.urn = jsonSerializedObject.urn;
          this.autoAllocateUrnAtStart = jsonSerializedObject.autoAllocateUrnAtStart;
          this.vesselFillColour = jsonSerializedObject.vesselFillColour;
          this.startDate = LocalDate.parse(jsonSerializedObject.startDate);
          this.batchTemplateId = jsonSerializedObject.batchTemplateId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.batchTemplateId);
          this.readOnly = new BatchHeaderReadOnly(jsonSerializedObject.readOnly);
          this.calculated = new BatchHeaderCalculated(jsonSerializedObject.calculated);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.isHidden = this.isHidden;
        toRet.materialProducedStockItemId = this.materialProducedStockItemId == null ? null : this.materialProducedStockItemId.toString();
        toRet.notes = this.notes;
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.urn = this.urn;
        toRet.autoAllocateUrnAtStart = this.autoAllocateUrnAtStart;
        toRet.vesselFillColour = this.vesselFillColour;
        toRet.startDate = this.startDate.toString();
        toRet.batchTemplateId = this.batchTemplateId == null ? null : this.batchTemplateId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.calculated = this.calculated.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "BatchHeader.Id cannot be empty"},
            {field: "materialProducedStockItemId", rule: (v: any) => v == null || !v.isEmpty() || "BatchHeader.MaterialProducedStockItemId cannot be empty"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "BatchHeader.StockLocationId cannot be empty"},
            {field: "urn", rule: (v: any) => v == null || v.length <= 1000 || "Urn has too many characters (1000)"},
            {field: "batchTemplateId", rule: (v: any) => v == null || !v.isEmpty() || "BatchHeader.BatchTemplateId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "calculated", rule: (v: any) => !!v || "calculated is required"},
            {field: "calculated", rule: (v: any) => v.validate()},
    ];
}


export class ProcessHeaderReadOnly extends ModelObject<ProcessHeaderReadOnly> {

    private _startedAt: Instant | null = null;
    /**
    * Timestamp at which the process was started.
    */
    public get startedAt(): Instant | null { return this._startedAt }
    public set startedAt(value: Instant | null) {
      this._startedAt = value === undefined ? null : value;
    }
    private _completedAt: Instant | null = null;
    /**
    * Timestamp at which the process was completed.
    */
    public get completedAt(): Instant | null { return this._completedAt }
    public set completedAt(value: Instant | null) {
      this._completedAt = value === undefined ? null : value;
    }
    private _costExpectedAtStart: number | null = null;
    /**
    * The cost which was expected as captured when the process was started.
    */
    public get costExpectedAtStart(): number | null { return this._costExpectedAtStart }
    public set costExpectedAtStart(value: number | null) {
      this._costExpectedAtStart = value === undefined ? null : value;
    }
    /**
    * Cannot be unset once set.
    */
    public hasPrintedLabels: boolean = false;
    /**
    * Timestamp at which the process header was last updated.
    */
    public lastUpdatedAt: Instant = Instant.ofEpochMilli(0);
    private _startedBy: Guid | null = null;
    /**
    * User who has started the process.
    */
    public get startedBy(): Guid | null { return this._startedBy }
    public set startedBy(value: Guid | null) {
      this._startedBy = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.startedAt = jsonSerializedObject.startedAt == null ? null : Instant.parse(jsonSerializedObject.startedAt);
          this.completedAt = jsonSerializedObject.completedAt == null ? null : Instant.parse(jsonSerializedObject.completedAt);
          this.costExpectedAtStart = jsonSerializedObject.costExpectedAtStart == null ? null : jsonSerializedObject.costExpectedAtStart;
          this.hasPrintedLabels = jsonSerializedObject.hasPrintedLabels;
          this.lastUpdatedAt = Instant.parse(jsonSerializedObject.lastUpdatedAt);
          this.startedBy = jsonSerializedObject.startedBy == null ? null : Guid.parseOrEmpty(jsonSerializedObject.startedBy);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.startedAt = this.startedAt == null ? null : this.startedAt.toString();
        toRet.completedAt = this.completedAt == null ? null : this.completedAt.toString();
        toRet.costExpectedAtStart = this.costExpectedAtStart == null ? null : this.costExpectedAtStart;
        toRet.hasPrintedLabels = this.hasPrintedLabels;
        toRet.lastUpdatedAt = this.lastUpdatedAt.toString();
        toRet.startedBy = this.startedBy == null ? null : this.startedBy.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "startedBy", rule: (v: any) => v == null || !v.isEmpty() || "ProcessHeaderReadOnly.StartedBy cannot be empty"},
    ];
}


export class ProcessHeaderCalculated extends ModelObject<ProcessHeaderCalculated> {

    private _outputExpectedAtStart: BatchOutput | null = null;
    public get outputExpectedAtStart(): BatchOutput | null { return this._outputExpectedAtStart }
    public set outputExpectedAtStart(value: BatchOutput | null) {
      this._outputExpectedAtStart = value === undefined ? null : value;
    }
    private _outputProduced: BatchOutput | null = null;
    public get outputProduced(): BatchOutput | null { return this._outputProduced }
    public set outputProduced(value: BatchOutput | null) {
      this._outputProduced = value === undefined ? null : value;
    }
    public status: EnumAssemblyStatus = EnumAssemblyStatus.open;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.outputExpectedAtStart = jsonSerializedObject.outputExpectedAtStart == null ? null : new BatchOutput(jsonSerializedObject.outputExpectedAtStart);
          this.outputProduced = jsonSerializedObject.outputProduced == null ? null : new BatchOutput(jsonSerializedObject.outputProduced);
          this.status = EnumAssemblyStatus.getByValue(jsonSerializedObject.status, this.status);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.outputExpectedAtStart = this.outputExpectedAtStart == null ? null : this.outputExpectedAtStart?.toJsonSerializedObject() ?? null;
        toRet.outputProduced = this.outputProduced == null ? null : this.outputProduced?.toJsonSerializedObject() ?? null;
        toRet.status = this.status.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "outputExpectedAtStart", rule: (v: any) => (v === null) || v.validate()},
            {field: "outputProduced", rule: (v: any) => (v === null) || v.validate()},
    ];
}


export class ProcessHeader extends ModelObject<ProcessHeader> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    private _batchId: Guid | null = null;
    /**
    * Id of the Batch where this Process Header belongs to. Null for ad hoc maintenance process.
    */
    public get batchId(): Guid | null { return this._batchId }
    public set batchId(value: Guid | null) {
      this._batchId = value === undefined ? null : value;
    }
    private _recipeId: Guid | null = null;
    /**
    * Recipe from which this process was initially created (if any). Does not drive any functionality, just for future reference really.
    */
    public get recipeId(): Guid | null { return this._recipeId }
    public set recipeId(value: Guid | null) {
      this._recipeId = value === undefined ? null : value;
    }
    /**
    * Assembly type of this process. : 0 = MaterialProduction, 1 = ProductPackaging, 2 = Maintenance
    */
    public processType: EnumAssemblyType = EnumAssemblyType.materialProduction;
    private _bestBefore: LocalDate | null = null;
    /**
    * Best Before Date
    */
    public get bestBefore(): LocalDate | null { return this._bestBefore }
    public set bestBefore(value: LocalDate | null) {
      this._bestBefore = value === undefined ? null : value;
    }
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Name.
    * Not Nullable
    * Max Length : 1000
    */
    public name: string = "";
    private _startDate: LocalDate | null = null;
    /**
    * Date at which the process was actually started - user editable (not captured by system). Not applicable to unstarted assemblies.
    */
    public get startDate(): LocalDate | null { return this._startDate }
    public set startDate(value: LocalDate | null) {
      this._startDate = value === undefined ? null : value;
    }
    /**
    * Duration this process is expected to take. Used for planning. Can be updated while process is in progress.
    */
    public expectedDurationDays: number = 0;
    private _doNotStartBeforeDate: LocalDate | null = null;
    /**
    * Date which the process should be delayed until if all prerequisites are already complete.  Particularly useful for planning packagings once produce is ready if the produce is to be held in a holding vessel and packaged in multiple steps.
    */
    public get doNotStartBeforeDate(): LocalDate | null { return this._doNotStartBeforeDate }
    public set doNotStartBeforeDate(value: LocalDate | null) {
      this._doNotStartBeforeDate = value === undefined ? null : value;
    }
    /**
    * Cannot be unset once set.
    */
    public hasPrintedLabels: boolean = false;
    /**
    * SubBatch
    * Not Nullable
    * Max Length : 1000
    */
    public subBatch: string = "";
    private _stockLocationId: Guid | null = null;
    /**
    * Required for ad hoc maintenance processes. Otherwise, use the Batch Stock Location Id.
    */
    public get stockLocationId(): Guid | null { return this._stockLocationId }
    public set stockLocationId(value: Guid | null) {
      this._stockLocationId = value === undefined ? null : value;
    }
    private _outputStillExpected: BatchOutput | null = null;
    public get outputStillExpected(): BatchOutput | null { return this._outputStillExpected }
    public set outputStillExpected(value: BatchOutput | null) {
      this._outputStillExpected = value === undefined ? null : value;
    }
    public readOnly: ProcessHeaderReadOnly = new ProcessHeaderReadOnly();
    public calculated: ProcessHeaderCalculated = new ProcessHeaderCalculated();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.batchId = jsonSerializedObject.batchId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.batchId);
          this.recipeId = jsonSerializedObject.recipeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.recipeId);
          this.processType = EnumAssemblyType.getByValue(jsonSerializedObject.processType, this.processType);
          this.bestBefore = jsonSerializedObject.bestBefore == null ? null : LocalDate.parse(jsonSerializedObject.bestBefore);
          this.notes = jsonSerializedObject.notes;
          this.name = jsonSerializedObject.name;
          this.startDate = jsonSerializedObject.startDate == null ? null : LocalDate.parse(jsonSerializedObject.startDate);
          this.expectedDurationDays = jsonSerializedObject.expectedDurationDays;
          this.doNotStartBeforeDate = jsonSerializedObject.doNotStartBeforeDate == null ? null : LocalDate.parse(jsonSerializedObject.doNotStartBeforeDate);
          this.hasPrintedLabels = jsonSerializedObject.hasPrintedLabels;
          this.subBatch = jsonSerializedObject.subBatch;
          this.stockLocationId = jsonSerializedObject.stockLocationId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.outputStillExpected = jsonSerializedObject.outputStillExpected == null ? null : new BatchOutput(jsonSerializedObject.outputStillExpected);
          this.readOnly = new ProcessHeaderReadOnly(jsonSerializedObject.readOnly);
          this.calculated = new ProcessHeaderCalculated(jsonSerializedObject.calculated);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.batchId = this.batchId == null ? null : this.batchId.toString();
        toRet.recipeId = this.recipeId == null ? null : this.recipeId.toString();
        toRet.processType = this.processType.value;
        toRet.bestBefore = this.bestBefore == null ? null : this.bestBefore.toString();
        toRet.notes = this.notes;
        toRet.name = this.name;
        toRet.startDate = this.startDate == null ? null : this.startDate.toString();
        toRet.expectedDurationDays = this.expectedDurationDays;
        toRet.doNotStartBeforeDate = this.doNotStartBeforeDate == null ? null : this.doNotStartBeforeDate.toString();
        toRet.hasPrintedLabels = this.hasPrintedLabels;
        toRet.subBatch = this.subBatch;
        toRet.stockLocationId = this.stockLocationId == null ? null : this.stockLocationId.toString();
        toRet.outputStillExpected = this.outputStillExpected == null ? null : this.outputStillExpected?.toJsonSerializedObject() ?? null;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.calculated = this.calculated.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "ProcessHeader.Id cannot be empty"},
            {field: "batchId", rule: (v: any) => v == null || !v.isEmpty() || "ProcessHeader.BatchId cannot be empty"},
            {field: "recipeId", rule: (v: any) => v == null || !v.isEmpty() || "ProcessHeader.RecipeId cannot be empty"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "expectedDurationDays", rule: (v: any) => v == null || v >= -2147483648 || "ExpectedDurationDays is not greater than minimum allowed value: -2147483648"},
            {field: "expectedDurationDays", rule: (v: any) => v == null || v <= 2147483647 || "ExpectedDurationDays is above maximum allowed value: 2147483647"},
            {field: "subBatch", rule: (v: any) => v == null || v.length <= 1000 || "SubBatch has too many characters (1000)"},
            {field: "stockLocationId", rule: (v: any) => v == null || !v.isEmpty() || "ProcessHeader.StockLocationId cannot be empty"},
            {field: "outputStillExpected", rule: (v: any) => (v === null) || v.validate()},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "calculated", rule: (v: any) => !!v || "calculated is required"},
            {field: "calculated", rule: (v: any) => v.validate()},
    ];
}


export class ProcessInstructionReadOnly extends ModelObject<ProcessInstructionReadOnly> {

    private _completedAt: Instant | null = null;
    /**
    * Timestamp at which the instruction was completed on the system. Captured by system not user editable.
    */
    public get completedAt(): Instant | null { return this._completedAt }
    public set completedAt(value: Instant | null) {
      this._completedAt = value === undefined ? null : value;
    }
    private _completedByUserId: Guid | null = null;
    /**
    * User Id of the user who completed the instruction. Captured by system not user editable.
    */
    public get completedByUserId(): Guid | null { return this._completedByUserId }
    public set completedByUserId(value: Guid | null) {
      this._completedByUserId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.completedAt = jsonSerializedObject.completedAt == null ? null : Instant.parse(jsonSerializedObject.completedAt);
          this.completedByUserId = jsonSerializedObject.completedByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.completedByUserId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.completedAt = this.completedAt == null ? null : this.completedAt.toString();
        toRet.completedByUserId = this.completedByUserId == null ? null : this.completedByUserId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "completedByUserId", rule: (v: any) => v == null || !v.isEmpty() || "ProcessInstructionReadOnly.CompletedByUserId cannot be empty"},
    ];
}


export class ProcessInstruction extends BaseModelObject<ProcessInstruction> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Id of the Process Header where this Instruction belongs to. 
    */
    public processId: Guid = Guid.createEmpty();
    /**
    * Sequence
    */
    public sequence: number = 0;
    /**
    * Instruction
    * Not Nullable
    * Max Length : 1000
    */
    public instruction: string = "";
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Complete
    */
    public complete: boolean = false;
    /**
    * Recorded Result
    * Not Nullable
    * Max Length : 100000
    */
    public recordedResult: string = "";
    public readOnly: ProcessInstructionReadOnly = new ProcessInstructionReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.processId = Guid.parseOrEmpty(jsonSerializedObject.processId);
          this.sequence = jsonSerializedObject.sequence;
          this.instruction = jsonSerializedObject.instruction;
          this.notes = jsonSerializedObject.notes;
          this.complete = jsonSerializedObject.complete;
          this.recordedResult = jsonSerializedObject.recordedResult;
          this.readOnly = new ProcessInstructionReadOnly(jsonSerializedObject.readOnly);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.processId = this.processId.toString();
        toRet.sequence = this.sequence;
        toRet.instruction = this.instruction;
        toRet.notes = this.notes;
        toRet.complete = this.complete;
        toRet.recordedResult = this.recordedResult;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "ProcessInstruction.Id cannot be empty"},
            {field: "processId", rule: (v: any) => !v.isEmpty() || "ProcessInstruction.ProcessId cannot be empty"},
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
            {field: "instruction", rule: (v: any) => v == null || v.length <= 1000 || "Instruction has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "recordedResult", rule: (v: any) => v == null || v.length <= 100000 || "RecordedResult has too many characters (100000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class ProcessVesselRequirement extends ModelObject<ProcessVesselRequirement> {

    public id: Guid = Guid.createEmpty();
    public version: number = 0;
    /**
    * Id of the Process Header to which this Requirement belongs. 
    */
    public processId: Guid = Guid.createEmpty();
    /**
    * Required Vessel Capability.
    */
    public vesselCapabilityId: Guid = Guid.createEmpty();
    /**
    * Required minimum capacity of Vessel in Litres
    * Not Negative
    */
    public capacityLitres: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.version = jsonSerializedObject.version;
          this.processId = Guid.parseOrEmpty(jsonSerializedObject.processId);
          this.vesselCapabilityId = Guid.parseOrEmpty(jsonSerializedObject.vesselCapabilityId);
          this.capacityLitres = jsonSerializedObject.capacityLitres;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.version = this.version;
        toRet.processId = this.processId.toString();
        toRet.vesselCapabilityId = this.vesselCapabilityId.toString();
        toRet.capacityLitres = this.capacityLitres;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "ProcessVesselRequirement.Id cannot be empty"},
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "processId", rule: (v: any) => !v.isEmpty() || "ProcessVesselRequirement.ProcessId cannot be empty"},
            {field: "vesselCapabilityId", rule: (v: any) => !v.isEmpty() || "ProcessVesselRequirement.VesselCapabilityId cannot be empty"},
    ];
}


export class ProcessProducedItemReadOnly extends ModelObject<ProcessProducedItemReadOnly> {

    /**
    * Timestamp at which the item produced line was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * Target Type this output went to. : 0 = Stock, 1 = ProcessSameBatch, 2 = ProcessOtherBatch
    */
    public targetType: EnumProcessOutputTarget = EnumProcessOutputTarget.stock;
    private _targetProcessId: Guid | null = null;
    /**
    * Id of Target Process if this output went to another process. Required if TargetType is ProcessSameBatch or ProcessOtherBatch.
    */
    public get targetProcessId(): Guid | null { return this._targetProcessId }
    public set targetProcessId(value: Guid | null) {
      this._targetProcessId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.targetType = EnumProcessOutputTarget.getByValue(jsonSerializedObject.targetType, this.targetType);
          this.targetProcessId = jsonSerializedObject.targetProcessId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.targetProcessId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.createdAt = this.createdAt.toString();
        toRet.targetType = this.targetType.value;
        toRet.targetProcessId = this.targetProcessId == null ? null : this.targetProcessId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "targetProcessId", rule: (v: any) => v == null || !v.isEmpty() || "ProcessProducedItemReadOnly.TargetProcessId cannot be empty"},
    ];
}


export class ProcessProducedItem extends ModelObject<ProcessProducedItem> {

    public id: Guid = Guid.createEmpty();
    /**
    * Process Id
    */
    public processId: Guid = Guid.createEmpty();
    /**
    * Stock item Id. All lines in one assembly must be for the same stock item.
    */
    public stockItemId: Guid = Guid.createEmpty();
    /**
    * Quantity Produced. A -ve value can act as a correction to a previous entry.
    */
    public quantityProduced: number = 0;
    /**
    * Unit of Measure
    */
    public unitOfMeasureId: Guid = Guid.createEmpty();
    /**
    * Whether this output was a by-product of the process rather than the main output item.
    */
    public isByProduct: boolean = false;
    /**
    * Unit cost price of the produced item.
    */
    public unitCostPrice: number = 0;
    public readOnly: ProcessProducedItemReadOnly = new ProcessProducedItemReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.processId = Guid.parseOrEmpty(jsonSerializedObject.processId);
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.quantityProduced = jsonSerializedObject.quantityProduced;
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.isByProduct = jsonSerializedObject.isByProduct;
          this.unitCostPrice = jsonSerializedObject.unitCostPrice;
          this.readOnly = new ProcessProducedItemReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.processId = this.processId.toString();
        toRet.stockItemId = this.stockItemId.toString();
        toRet.quantityProduced = this.quantityProduced;
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.isByProduct = this.isByProduct;
        toRet.unitCostPrice = this.unitCostPrice;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "ProcessProducedItem.Id cannot be empty"},
            {field: "processId", rule: (v: any) => !v.isEmpty() || "ProcessProducedItem.ProcessId cannot be empty"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "ProcessProducedItem.StockItemId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "ProcessProducedItem.UnitOfMeasureId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class ProcessIngredientLineReadOnly extends ModelObject<ProcessIngredientLineReadOnly> {

    private _quantityExpectedAtStart: number | null = null;
    /**
    * Quantity expected to be used as captured at the time the process was started (null if not started).
    */
    public get quantityExpectedAtStart(): number | null { return this._quantityExpectedAtStart }
    public set quantityExpectedAtStart(value: number | null) {
      this._quantityExpectedAtStart = value === undefined ? null : value;
    }
    private _totalCostExpectedAtStart: number | null = null;
    /**
    * Total cost of ingredient expected as captured at the time the process was started. (Quantity * StockItem Cost Price at time of start). (null if not started).
    */
    public get totalCostExpectedAtStart(): number | null { return this._totalCostExpectedAtStart }
    public set totalCostExpectedAtStart(value: number | null) {
      this._totalCostExpectedAtStart = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.quantityExpectedAtStart = jsonSerializedObject.quantityExpectedAtStart == null ? null : jsonSerializedObject.quantityExpectedAtStart;
          this.totalCostExpectedAtStart = jsonSerializedObject.totalCostExpectedAtStart == null ? null : jsonSerializedObject.totalCostExpectedAtStart;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.quantityExpectedAtStart = this.quantityExpectedAtStart == null ? null : this.quantityExpectedAtStart;
        toRet.totalCostExpectedAtStart = this.totalCostExpectedAtStart == null ? null : this.totalCostExpectedAtStart;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class ProcessIngredientLine extends BaseModelObject<ProcessIngredientLine> {

    public id: Guid = Guid.createEmpty();
    public version: number = 0;
    /**
    * Parent Process Id, can not be changed after creation of the ingredient line.
    */
    public processId: Guid = Guid.createEmpty();
    /**
    * Sequence (for ordering Lines).
    */
    public sequence: number = 0;
    /**
    * Quantity Still expected to be used.
    */
    public quantityStillExpected: number = 0;
    /**
    * Ingredient stock item, can not be changed if any ingredient used lines exist.
    */
    public stockItemId: Guid = Guid.createEmpty();
    /**
    * Unit of Measure
    */
    public unitOfMeasureId: Guid = Guid.createEmpty();
    /**
    * Whether this ingredient is fixed to its current quantity regardless of adjustments to process output.If set to true, the ingredient required quantity will not be multiplied when adjusting planned process output quantities.
    */
    public fixedQuantity: boolean = false;
    /**
    * Notes.
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Enables hop alpha scaling for this ingredient if the item and stock batch have hop alpha values.
    */
    public allowsHopAlpha: boolean = false;
    public readOnly: ProcessIngredientLineReadOnly = new ProcessIngredientLineReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.version = jsonSerializedObject.version;
          this.processId = Guid.parseOrEmpty(jsonSerializedObject.processId);
          this.sequence = jsonSerializedObject.sequence;
          this.quantityStillExpected = jsonSerializedObject.quantityStillExpected;
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.fixedQuantity = jsonSerializedObject.fixedQuantity;
          this.notes = jsonSerializedObject.notes;
          this.allowsHopAlpha = jsonSerializedObject.allowsHopAlpha;
          this.readOnly = new ProcessIngredientLineReadOnly(jsonSerializedObject.readOnly);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.version = this.version;
        toRet.processId = this.processId.toString();
        toRet.sequence = this.sequence;
        toRet.quantityStillExpected = this.quantityStillExpected;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.fixedQuantity = this.fixedQuantity;
        toRet.notes = this.notes;
        toRet.allowsHopAlpha = this.allowsHopAlpha;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "ProcessIngredientLine.Id cannot be empty"},
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "processId", rule: (v: any) => !v.isEmpty() || "ProcessIngredientLine.ProcessId cannot be empty"},
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "ProcessIngredientLine.StockItemId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "ProcessIngredientLine.UnitOfMeasureId cannot be empty"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class ProcessIngredientUsedLineReadOnly extends ModelObject<ProcessIngredientUsedLineReadOnly> {

    /**
    * Timestamp at which the ingredient used line was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    private _sourceProcessId: Guid | null = null;
    /**
    * Id of Source Process if this ingredient came from another process. Required if SourceType is ProcessSameBatch or ProcessOtherBatch.
    */
    public get sourceProcessId(): Guid | null { return this._sourceProcessId }
    public set sourceProcessId(value: Guid | null) {
      this._sourceProcessId = value === undefined ? null : value;
    }
    /**
    * Source Type this ingredient came from. : 0 = Stock, 1 = ProcessSameBatch, 2 = ProcessOtherBatch
    */
    public sourceType: EnumProcessIngredientSource = EnumProcessIngredientSource.stock;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.sourceProcessId = jsonSerializedObject.sourceProcessId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.sourceProcessId);
          this.sourceType = EnumProcessIngredientSource.getByValue(jsonSerializedObject.sourceType, this.sourceType);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.createdAt = this.createdAt.toString();
        toRet.sourceProcessId = this.sourceProcessId == null ? null : this.sourceProcessId.toString();
        toRet.sourceType = this.sourceType.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "sourceProcessId", rule: (v: any) => v == null || !v.isEmpty() || "ProcessIngredientUsedLineReadOnly.SourceProcessId cannot be empty"},
    ];
}


export class ProcessIngredientUsedLine extends BaseModelObject<ProcessIngredientUsedLine> {

    public id: Guid = Guid.createEmpty();
    /**
    * Parent Ingredient Line Id, can not be changed after creation of ingredient used line.
    */
    public processIngredientLineId: Guid = Guid.createEmpty();
    /**
    * Cannot be changed after creation of ingredient used line.
    */
    public quantityUsed: number = 0;
    /**
    * Total cost of ingredients used in this line.
    */
    public totalCost: number = 0;
    /**
    * True if the cost is now known, False if it may be updated automatically, due to completion of the source batch.
    */
    public isCostFinalised: boolean = false;
    /**
    * Must match the ingredient line item unit of measure.
    */
    public unitOfMeasureId: Guid = Guid.createEmpty();
    /**
    * Cannot be changed after creation of ingredient used line.
    * Not Nullable
    * Max Length : 1000
    */
    public urn: string = "";
    public readOnly: ProcessIngredientUsedLineReadOnly = new ProcessIngredientUsedLineReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.processIngredientLineId = Guid.parseOrEmpty(jsonSerializedObject.processIngredientLineId);
          this.quantityUsed = jsonSerializedObject.quantityUsed;
          this.totalCost = jsonSerializedObject.totalCost;
          this.isCostFinalised = jsonSerializedObject.isCostFinalised;
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.urn = jsonSerializedObject.urn;
          this.readOnly = new ProcessIngredientUsedLineReadOnly(jsonSerializedObject.readOnly);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.processIngredientLineId = this.processIngredientLineId.toString();
        toRet.quantityUsed = this.quantityUsed;
        toRet.totalCost = this.totalCost;
        toRet.isCostFinalised = this.isCostFinalised;
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.urn = this.urn;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "ProcessIngredientUsedLine.Id cannot be empty"},
            {field: "processIngredientLineId", rule: (v: any) => !v.isEmpty() || "ProcessIngredientUsedLine.ProcessIngredientLineId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "ProcessIngredientUsedLine.UnitOfMeasureId cannot be empty"},
            {field: "urn", rule: (v: any) => v == null || v.length <= 1000 || "Urn has too many characters (1000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class ProcessIngredient extends ModelObject<ProcessIngredient> {

    public line: ProcessIngredientLine = new ProcessIngredientLine();
    public usedLines: ProcessIngredientUsedLine[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.line = new ProcessIngredientLine(jsonSerializedObject.line);
          this.usedLines = jsonSerializedObject.usedLines.map((v: any) => new ProcessIngredientUsedLine(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.line = this.line.toJsonSerializedObject();
        toRet.usedLines = this.usedLines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "line", rule: (v: any) => !!v || "line is required"},
            {field: "line", rule: (v: any) => v.validate()},
            {field: "usedLines", rule: (v: any) => !!v || "usedLines is required"},
            {field: "usedLines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as ProcessIngredientUsedLine[]).reduce((acc: Array<string>, curr: ProcessIngredientUsedLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class Process extends ModelObject<Process> {

    public header: ProcessHeader = new ProcessHeader();
    public instructions: ProcessInstruction[] = [];
    public prerequisites: Guid[] = [];
    public vesselRequirements: ProcessVesselRequirement[] = [];
    public producedItems: ProcessProducedItem[] = [];
    public ingredients: ProcessIngredient[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new ProcessHeader(jsonSerializedObject.header);
          this.instructions = jsonSerializedObject.instructions.map((v: any) => new ProcessInstruction(v));
          this.prerequisites = jsonSerializedObject.prerequisites.map((v: any) => Guid.parseOrEmpty(v));
          this.vesselRequirements = jsonSerializedObject.vesselRequirements.map((v: any) => new ProcessVesselRequirement(v));
          this.producedItems = jsonSerializedObject.producedItems.map((v: any) => new ProcessProducedItem(v));
          this.ingredients = jsonSerializedObject.ingredients.map((v: any) => new ProcessIngredient(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.instructions = this.instructions.map(v => v.toJsonSerializedObject());
        toRet.prerequisites = this.prerequisites.map(v => v.toString());
        toRet.vesselRequirements = this.vesselRequirements.map(v => v.toJsonSerializedObject());
        toRet.producedItems = this.producedItems.map(v => v.toJsonSerializedObject());
        toRet.ingredients = this.ingredients.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "instructions", rule: (v: any) => !!v || "instructions is required"},
            {field: "instructions", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as ProcessInstruction[]).reduce((acc: Array<string>, curr: ProcessInstruction) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "vesselRequirements", rule: (v: any) => !!v || "vesselRequirements is required"},
            {field: "vesselRequirements", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as ProcessVesselRequirement[]).reduce((acc: Array<string>, curr: ProcessVesselRequirement) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "producedItems", rule: (v: any) => !!v || "producedItems is required"},
            {field: "producedItems", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as ProcessProducedItem[]).reduce((acc: Array<string>, curr: ProcessProducedItem) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "ingredients", rule: (v: any) => !!v || "ingredients is required"},
            {field: "ingredients", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as ProcessIngredient[]).reduce((acc: Array<string>, curr: ProcessIngredient) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class Batch extends ModelObject<Batch> {

    public header: BatchHeader = new BatchHeader();
    public processes: Process[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new BatchHeader(jsonSerializedObject.header);
          this.processes = jsonSerializedObject.processes.map((v: any) => new Process(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.processes = this.processes.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "processes", rule: (v: any) => !!v || "processes is required"},
            {field: "processes", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as Process[]).reduce((acc: Array<string>, curr: Process) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class IngredientRecommendedBatch extends ModelObject<IngredientRecommendedBatch> {

    public ingredientLineId: Guid = Guid.createEmpty();
    public processId: Guid = Guid.createEmpty();
    public stockLocationId: Guid = Guid.createEmpty();
    public stockItemId: Guid = Guid.createEmpty();
    private _availableQuantity: number | null = null;
    public get availableQuantity(): number | null { return this._availableQuantity }
    public set availableQuantity(value: number | null) {
      this._availableQuantity = value === undefined ? null : value;
    }
    private _recommendedBatch: Guid | null = null;
    public get recommendedBatch(): Guid | null { return this._recommendedBatch }
    public set recommendedBatch(value: Guid | null) {
      this._recommendedBatch = value === undefined ? null : value;
    }
    public recommendedBatchUrn: string = "";
    private _recommendedBatchHopAlpha: number | null = null;
    public get recommendedBatchHopAlpha(): number | null { return this._recommendedBatchHopAlpha }
    public set recommendedBatchHopAlpha(value: number | null) {
      this._recommendedBatchHopAlpha = value === undefined ? null : value;
    }
    public recommendedQuantity: number = 0;
    public requiredQuantity: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.ingredientLineId = Guid.parseOrEmpty(jsonSerializedObject.ingredientLineId);
          this.processId = Guid.parseOrEmpty(jsonSerializedObject.processId);
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.availableQuantity = jsonSerializedObject.availableQuantity == null ? null : jsonSerializedObject.availableQuantity;
          this.recommendedBatch = jsonSerializedObject.recommendedBatch == null ? null : Guid.parseOrEmpty(jsonSerializedObject.recommendedBatch);
          this.recommendedBatchUrn = jsonSerializedObject.recommendedBatchUrn;
          this.recommendedBatchHopAlpha = jsonSerializedObject.recommendedBatchHopAlpha == null ? null : jsonSerializedObject.recommendedBatchHopAlpha;
          this.recommendedQuantity = jsonSerializedObject.recommendedQuantity;
          this.requiredQuantity = jsonSerializedObject.requiredQuantity;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.ingredientLineId = this.ingredientLineId.toString();
        toRet.processId = this.processId.toString();
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.stockItemId = this.stockItemId.toString();
        toRet.availableQuantity = this.availableQuantity == null ? null : this.availableQuantity;
        toRet.recommendedBatch = this.recommendedBatch == null ? null : this.recommendedBatch.toString();
        toRet.recommendedBatchUrn = this.recommendedBatchUrn;
        toRet.recommendedBatchHopAlpha = this.recommendedBatchHopAlpha == null ? null : this.recommendedBatchHopAlpha;
        toRet.recommendedQuantity = this.recommendedQuantity;
        toRet.requiredQuantity = this.requiredQuantity;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "ingredientLineId", rule: (v: any) => !v.isEmpty() || "IngredientRecommendedBatch.IngredientLineId cannot be empty"},
            {field: "processId", rule: (v: any) => !v.isEmpty() || "IngredientRecommendedBatch.ProcessId cannot be empty"},
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "IngredientRecommendedBatch.StockLocationId cannot be empty"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "IngredientRecommendedBatch.StockItemId cannot be empty"},
            {field: "recommendedBatch", rule: (v: any) => v == null || !v.isEmpty() || "IngredientRecommendedBatch.RecommendedBatch cannot be empty"},
    ];
}


export class SectionsControl extends ModelObject<SectionsControl> {

    public showDetailsRecordedSection: boolean = false;
    public showIngredientSection: boolean = false;
    public showProcessSection: boolean = false;
    public showQualityControlSection: boolean = false;
    public showVolumeVesselSection: boolean = false;
    public showOutputSection: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.showDetailsRecordedSection = jsonSerializedObject.showDetailsRecordedSection;
          this.showIngredientSection = jsonSerializedObject.showIngredientSection;
          this.showProcessSection = jsonSerializedObject.showProcessSection;
          this.showQualityControlSection = jsonSerializedObject.showQualityControlSection;
          this.showVolumeVesselSection = jsonSerializedObject.showVolumeVesselSection;
          this.showOutputSection = jsonSerializedObject.showOutputSection;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.showDetailsRecordedSection = this.showDetailsRecordedSection;
        toRet.showIngredientSection = this.showIngredientSection;
        toRet.showProcessSection = this.showProcessSection;
        toRet.showQualityControlSection = this.showQualityControlSection;
        toRet.showVolumeVesselSection = this.showVolumeVesselSection;
        toRet.showOutputSection = this.showOutputSection;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class ProcessCostSummary extends ModelObject<ProcessCostSummary> {

    public processId: Guid = Guid.createEmpty();
    private _producedStockItemId: Guid | null = null;
    public get producedStockItemId(): Guid | null { return this._producedStockItemId }
    public set producedStockItemId(value: Guid | null) {
      this._producedStockItemId = value === undefined ? null : value;
    }
    public totalIngredientsCost: number = 0;
    /**
    * Total quantity of outputs to process of the same batch.
    */
    public outputQuantityStayingInBatch: number = 0;
    public outputQuantityToOtherBatch: number = 0;
    public outputQuantityToStock: number = 0;
    public batchWastageCostApportionedToProcess: number = 0;
    public calculatedPricePerUnitForOutputStayingInBatch: number = 0;
    public calculatedPricePerUnitForOutputLeavingBatch: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.processId = Guid.parseOrEmpty(jsonSerializedObject.processId);
          this.producedStockItemId = jsonSerializedObject.producedStockItemId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.producedStockItemId);
          this.totalIngredientsCost = jsonSerializedObject.totalIngredientsCost;
          this.outputQuantityStayingInBatch = jsonSerializedObject.outputQuantityStayingInBatch;
          this.outputQuantityToOtherBatch = jsonSerializedObject.outputQuantityToOtherBatch;
          this.outputQuantityToStock = jsonSerializedObject.outputQuantityToStock;
          this.batchWastageCostApportionedToProcess = jsonSerializedObject.batchWastageCostApportionedToProcess;
          this.calculatedPricePerUnitForOutputStayingInBatch = jsonSerializedObject.calculatedPricePerUnitForOutputStayingInBatch;
          this.calculatedPricePerUnitForOutputLeavingBatch = jsonSerializedObject.calculatedPricePerUnitForOutputLeavingBatch;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.processId = this.processId.toString();
        toRet.producedStockItemId = this.producedStockItemId == null ? null : this.producedStockItemId.toString();
        toRet.totalIngredientsCost = this.totalIngredientsCost;
        toRet.outputQuantityStayingInBatch = this.outputQuantityStayingInBatch;
        toRet.outputQuantityToOtherBatch = this.outputQuantityToOtherBatch;
        toRet.outputQuantityToStock = this.outputQuantityToStock;
        toRet.batchWastageCostApportionedToProcess = this.batchWastageCostApportionedToProcess;
        toRet.calculatedPricePerUnitForOutputStayingInBatch = this.calculatedPricePerUnitForOutputStayingInBatch;
        toRet.calculatedPricePerUnitForOutputLeavingBatch = this.calculatedPricePerUnitForOutputLeavingBatch;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "processId", rule: (v: any) => !v.isEmpty() || "ProcessCostSummary.ProcessId cannot be empty"},
            {field: "producedStockItemId", rule: (v: any) => v == null || !v.isEmpty() || "ProcessCostSummary.ProducedStockItemId cannot be empty"},
    ];
}


export class BatchFilter extends ModelObject<BatchFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _statuses: EnumAssemblyStatus[] | null = null;
    /**
    * If provided only matching statuses are returned
    */
    public get statuses(): EnumAssemblyStatus[] | null { return this._statuses }
    public set statuses(value: EnumAssemblyStatus[] | null) {
      this._statuses = value === undefined ? null : value;
    }
    private _stockLocations: Guid[] | null = null;
    public get stockLocations(): Guid[] | null { return this._stockLocations }
    public set stockLocations(value: Guid[] | null) {
      this._stockLocations = value === undefined ? null : value;
    }
    /**
    * Yes = Hidden Only, No = Not Hidden Only, Either = Either
    */
    public hiddenOrNot: EnumYesNoEither = EnumYesNoEither.either;
    private _batchIds: Guid[] | null = null;
    public get batchIds(): Guid[] | null { return this._batchIds }
    public set batchIds(value: Guid[] | null) {
      this._batchIds = value === undefined ? null : value;
    }
    private _fromStartDateInclusive: LocalDate | null = null;
    /**
    * Minimum Start Date
    */
    public get fromStartDateInclusive(): LocalDate | null { return this._fromStartDateInclusive }
    public set fromStartDateInclusive(value: LocalDate | null) {
      this._fromStartDateInclusive = value === undefined ? null : value;
    }
    private _toStartDateInclusive: LocalDate | null = null;
    /**
    * Maximum Start Date
    */
    public get toStartDateInclusive(): LocalDate | null { return this._toStartDateInclusive }
    public set toStartDateInclusive(value: LocalDate | null) {
      this._toStartDateInclusive = value === undefined ? null : value;
    }
    private _fromCompletedDateInclusive: LocalDate | null = null;
    /**
    * Minimum Completed Date
    */
    public get fromCompletedDateInclusive(): LocalDate | null { return this._fromCompletedDateInclusive }
    public set fromCompletedDateInclusive(value: LocalDate | null) {
      this._fromCompletedDateInclusive = value === undefined ? null : value;
    }
    private _toCompletedDateInclusive: LocalDate | null = null;
    /**
    * Maximum Completed Date
    */
    public get toCompletedDateInclusive(): LocalDate | null { return this._toCompletedDateInclusive }
    public set toCompletedDateInclusive(value: LocalDate | null) {
      this._toCompletedDateInclusive = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.statuses = jsonSerializedObject.statuses == null ? null : jsonSerializedObject.statuses.map((v: any) => EnumAssemblyStatus.getByValue(v));
          this.stockLocations = jsonSerializedObject.stockLocations == null ? null : jsonSerializedObject.stockLocations.map((v: any) => Guid.parseOrEmpty(v));
          this.hiddenOrNot = EnumYesNoEither.getByValue(jsonSerializedObject.hiddenOrNot, this.hiddenOrNot);
          this.batchIds = jsonSerializedObject.batchIds == null ? null : jsonSerializedObject.batchIds.map((v: any) => Guid.parseOrEmpty(v));
          this.fromStartDateInclusive = jsonSerializedObject.fromStartDateInclusive == null ? null : LocalDate.parse(jsonSerializedObject.fromStartDateInclusive);
          this.toStartDateInclusive = jsonSerializedObject.toStartDateInclusive == null ? null : LocalDate.parse(jsonSerializedObject.toStartDateInclusive);
          this.fromCompletedDateInclusive = jsonSerializedObject.fromCompletedDateInclusive == null ? null : LocalDate.parse(jsonSerializedObject.fromCompletedDateInclusive);
          this.toCompletedDateInclusive = jsonSerializedObject.toCompletedDateInclusive == null ? null : LocalDate.parse(jsonSerializedObject.toCompletedDateInclusive);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.statuses = this.statuses == null ? null : this.statuses.map(v => v.value);
        toRet.stockLocations = this.stockLocations == null ? null : this.stockLocations.map(v => v.toString());
        toRet.hiddenOrNot = this.hiddenOrNot.value;
        toRet.batchIds = this.batchIds == null ? null : this.batchIds.map(v => v.toString());
        toRet.fromStartDateInclusive = this.fromStartDateInclusive == null ? null : this.fromStartDateInclusive.toString();
        toRet.toStartDateInclusive = this.toStartDateInclusive == null ? null : this.toStartDateInclusive.toString();
        toRet.fromCompletedDateInclusive = this.fromCompletedDateInclusive == null ? null : this.fromCompletedDateInclusive.toString();
        toRet.toCompletedDateInclusive = this.toCompletedDateInclusive == null ? null : this.toCompletedDateInclusive.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredBatches extends ModelObject<FilteredBatches> {

    public matchingCount: number = 0;
    public batches: Batch[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.batches = jsonSerializedObject.batches.map((v: any) => new Batch(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.batches = this.batches.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "batches", rule: (v: any) => !!v || "batches is required"},
            {field: "batches", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as Batch[]).reduce((acc: Array<string>, curr: Batch) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class BatchLiquidEvaluation extends ModelObject<BatchLiquidEvaluation> {

    public batchId: Guid = Guid.createEmpty();
    public stockLocationId: Guid = Guid.createEmpty();
    public heldAtStartOfPeriodLitres: number = 0;
    public heldAtEndOfPeriodLitres: number = 0;
    public producedLitres: number = 0;
    public reprocessedLitres: number = 0;
    public discardedLitres: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.batchId = Guid.parseOrEmpty(jsonSerializedObject.batchId);
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.heldAtStartOfPeriodLitres = jsonSerializedObject.heldAtStartOfPeriodLitres;
          this.heldAtEndOfPeriodLitres = jsonSerializedObject.heldAtEndOfPeriodLitres;
          this.producedLitres = jsonSerializedObject.producedLitres;
          this.reprocessedLitres = jsonSerializedObject.reprocessedLitres;
          this.discardedLitres = jsonSerializedObject.discardedLitres;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.batchId = this.batchId.toString();
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.heldAtStartOfPeriodLitres = this.heldAtStartOfPeriodLitres;
        toRet.heldAtEndOfPeriodLitres = this.heldAtEndOfPeriodLitres;
        toRet.producedLitres = this.producedLitres;
        toRet.reprocessedLitres = this.reprocessedLitres;
        toRet.discardedLitres = this.discardedLitres;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "batchId", rule: (v: any) => !v.isEmpty() || "BatchLiquidEvaluation.BatchId cannot be empty"},
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "BatchLiquidEvaluation.StockLocationId cannot be empty"},
    ];
}


export class FilteredBatchHeaders extends ModelObject<FilteredBatchHeaders> {

    public matchingCount: number = 0;
    public batchHeaders: BatchHeader[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.batchHeaders = jsonSerializedObject.batchHeaders.map((v: any) => new BatchHeader(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.batchHeaders = this.batchHeaders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "batchHeaders", rule: (v: any) => !!v || "batchHeaders is required"},
            {field: "batchHeaders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as BatchHeader[]).reduce((acc: Array<string>, curr: BatchHeader) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class ProcessFilter extends ModelObject<ProcessFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _fromStartDateInclusive: LocalDate | null = null;
    /**
    * Minimum Start Date
    */
    public get fromStartDateInclusive(): LocalDate | null { return this._fromStartDateInclusive }
    public set fromStartDateInclusive(value: LocalDate | null) {
      this._fromStartDateInclusive = value === undefined ? null : value;
    }
    private _toStartDateInclusive: LocalDate | null = null;
    /**
    * Maximum Start Date
    */
    public get toStartDateInclusive(): LocalDate | null { return this._toStartDateInclusive }
    public set toStartDateInclusive(value: LocalDate | null) {
      this._toStartDateInclusive = value === undefined ? null : value;
    }
    private _batchIds: Guid[] | null = null;
    public get batchIds(): Guid[] | null { return this._batchIds }
    public set batchIds(value: Guid[] | null) {
      this._batchIds = value === undefined ? null : value;
    }
    private _statuses: EnumAssemblyStatus[] | null = null;
    public get statuses(): EnumAssemblyStatus[] | null { return this._statuses }
    public set statuses(value: EnumAssemblyStatus[] | null) {
      this._statuses = value === undefined ? null : value;
    }
    private _processTypes: EnumAssemblyType[] | null = null;
    public get processTypes(): EnumAssemblyType[] | null { return this._processTypes }
    public set processTypes(value: EnumAssemblyType[] | null) {
      this._processTypes = value === undefined ? null : value;
    }
    public includeAdHocMaintenanceProcess: EnumYesNoEither = EnumYesNoEither.either;
    private _hasOutput: boolean | null = null;
    /**
    * Returns processes with an output
    */
    public get hasOutput(): boolean | null { return this._hasOutput }
    public set hasOutput(value: boolean | null) {
      this._hasOutput = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.fromStartDateInclusive = jsonSerializedObject.fromStartDateInclusive == null ? null : LocalDate.parse(jsonSerializedObject.fromStartDateInclusive);
          this.toStartDateInclusive = jsonSerializedObject.toStartDateInclusive == null ? null : LocalDate.parse(jsonSerializedObject.toStartDateInclusive);
          this.batchIds = jsonSerializedObject.batchIds == null ? null : jsonSerializedObject.batchIds.map((v: any) => Guid.parseOrEmpty(v));
          this.statuses = jsonSerializedObject.statuses == null ? null : jsonSerializedObject.statuses.map((v: any) => EnumAssemblyStatus.getByValue(v));
          this.processTypes = jsonSerializedObject.processTypes == null ? null : jsonSerializedObject.processTypes.map((v: any) => EnumAssemblyType.getByValue(v));
          this.includeAdHocMaintenanceProcess = EnumYesNoEither.getByValue(jsonSerializedObject.includeAdHocMaintenanceProcess, this.includeAdHocMaintenanceProcess);
          this.hasOutput = jsonSerializedObject.hasOutput == null ? null : jsonSerializedObject.hasOutput;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.fromStartDateInclusive = this.fromStartDateInclusive == null ? null : this.fromStartDateInclusive.toString();
        toRet.toStartDateInclusive = this.toStartDateInclusive == null ? null : this.toStartDateInclusive.toString();
        toRet.batchIds = this.batchIds == null ? null : this.batchIds.map(v => v.toString());
        toRet.statuses = this.statuses == null ? null : this.statuses.map(v => v.value);
        toRet.processTypes = this.processTypes == null ? null : this.processTypes.map(v => v.value);
        toRet.includeAdHocMaintenanceProcess = this.includeAdHocMaintenanceProcess.value;
        toRet.hasOutput = this.hasOutput == null ? null : this.hasOutput;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredProcesses extends ModelObject<FilteredProcesses> {

    public matchingCount: number = 0;
    public processes: Process[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.processes = jsonSerializedObject.processes.map((v: any) => new Process(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.processes = this.processes.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "processes", rule: (v: any) => !!v || "processes is required"},
            {field: "processes", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as Process[]).reduce((acc: Array<string>, curr: Process) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class ProcessStockExpectationLine extends ModelObject<ProcessStockExpectationLine> {

    public processId: Guid = Guid.createEmpty();
    public status: EnumAssemblyStatus = EnumAssemblyStatus.open;
    public expectedCompletionDate: LocalDate = LocalDate.ofEpochDay(0);
    public stockLocationId: Guid = Guid.createEmpty();
    /**
    * Expected stock change. +ve means stock will increase.
    */
    public signedQuantity: number = 0;
    /**
    * Identifies if this is an expected ingredient (opposed to expected output). Explicit as sign of SignedQuantity may not correlate.
    */
    public isIngredient: boolean = false;
    public stockItemId: Guid = Guid.createEmpty();
    public unitOfMeasureId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.processId = Guid.parseOrEmpty(jsonSerializedObject.processId);
          this.status = EnumAssemblyStatus.getByValue(jsonSerializedObject.status, this.status);
          this.expectedCompletionDate = LocalDate.parse(jsonSerializedObject.expectedCompletionDate);
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.signedQuantity = jsonSerializedObject.signedQuantity;
          this.isIngredient = jsonSerializedObject.isIngredient;
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.processId = this.processId.toString();
        toRet.status = this.status.value;
        toRet.expectedCompletionDate = this.expectedCompletionDate.toString();
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.signedQuantity = this.signedQuantity;
        toRet.isIngredient = this.isIngredient;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "processId", rule: (v: any) => !v.isEmpty() || "ProcessStockExpectationLine.ProcessId cannot be empty"},
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "ProcessStockExpectationLine.StockLocationId cannot be empty"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "ProcessStockExpectationLine.StockItemId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "ProcessStockExpectationLine.UnitOfMeasureId cannot be empty"},
    ];
}


export class ProcessStockFilter extends ModelObject<ProcessStockFilter> {

    public includeIngredientsUsedFromStock: boolean = false;
    public includeOutputsToStock: boolean = false;
    private _fromInstant: Instant | null = null;
    /**
    * Timestamp of when the ingredient is used or when process outputs to stock.
    */
    public get fromInstant(): Instant | null { return this._fromInstant }
    public set fromInstant(value: Instant | null) {
      this._fromInstant = value === undefined ? null : value;
    }
    private _beforeInstant: Instant | null = null;
    /**
    * Timestamp of when the ingredient is used or when process outputs to stock.
    */
    public get beforeInstant(): Instant | null { return this._beforeInstant }
    public set beforeInstant(value: Instant | null) {
      this._beforeInstant = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.includeIngredientsUsedFromStock = jsonSerializedObject.includeIngredientsUsedFromStock;
          this.includeOutputsToStock = jsonSerializedObject.includeOutputsToStock;
          this.fromInstant = jsonSerializedObject.fromInstant == null ? null : Instant.parse(jsonSerializedObject.fromInstant);
          this.beforeInstant = jsonSerializedObject.beforeInstant == null ? null : Instant.parse(jsonSerializedObject.beforeInstant);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.includeIngredientsUsedFromStock = this.includeIngredientsUsedFromStock;
        toRet.includeOutputsToStock = this.includeOutputsToStock;
        toRet.fromInstant = this.fromInstant == null ? null : this.fromInstant.toString();
        toRet.beforeInstant = this.beforeInstant == null ? null : this.beforeInstant.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class ProcessBlockingQcTest extends ModelObject<ProcessBlockingQcTest> {

    public processId: Guid = Guid.createEmpty();
    public qcTestId: Guid = Guid.createEmpty();
    public name: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.processId = Guid.parseOrEmpty(jsonSerializedObject.processId);
          this.qcTestId = Guid.parseOrEmpty(jsonSerializedObject.qcTestId);
          this.name = jsonSerializedObject.name;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.processId = this.processId.toString();
        toRet.qcTestId = this.qcTestId.toString();
        toRet.name = this.name;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "processId", rule: (v: any) => !v.isEmpty() || "ProcessBlockingQcTest.ProcessId cannot be empty"},
            {field: "qcTestId", rule: (v: any) => !v.isEmpty() || "ProcessBlockingQcTest.QcTestId cannot be empty"},
    ];
}


export class FilteredProcessHeaders extends ModelObject<FilteredProcessHeaders> {

    public matchingCount: number = 0;
    public processHeaders: ProcessHeader[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.processHeaders = jsonSerializedObject.processHeaders.map((v: any) => new ProcessHeader(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.processHeaders = this.processHeaders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "processHeaders", rule: (v: any) => !!v || "processHeaders is required"},
            {field: "processHeaders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as ProcessHeader[]).reduce((acc: Array<string>, curr: ProcessHeader) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class ProcessPrerequisite extends ModelObject<ProcessPrerequisite> {

    /**
    * Id of the Process Header where this Prerequisite belongs to.
    */
    public processId: Guid = Guid.createEmpty();
    /**
    * Id of the Process Header that must be completed as a prerequisite before the current ProcessId.
    */
    public prerequisiteProcessId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.processId = Guid.parseOrEmpty(jsonSerializedObject.processId);
          this.prerequisiteProcessId = Guid.parseOrEmpty(jsonSerializedObject.prerequisiteProcessId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.processId = this.processId.toString();
        toRet.prerequisiteProcessId = this.prerequisiteProcessId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "processId", rule: (v: any) => !v.isEmpty() || "ProcessPrerequisite.ProcessId cannot be empty"},
            {field: "prerequisiteProcessId", rule: (v: any) => !v.isEmpty() || "ProcessPrerequisite.PrerequisiteProcessId cannot be empty"},
    ];
}


export class BatchService extends ServiceBase {

  public async getBatch(tenantId: Guid, batchId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Batch> {
    const req = {
    tenantId:tenantId.toString(),
    batchId:batchId.toString(),
    };
    const extractor = (response:any) => new Batch(response.batch);;
    return this.makeJsonRequest("batch/v1/getBatch", req, false, extractor, opts);
  };

  public async getBatches(tenantId: Guid, batchIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Batch[]> {
    const req = {
    tenantId:tenantId.toString(),
    batchIds:batchIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.batches.map((v: any) => new Batch(v));;
    return this.makeJsonRequest("batch/v1/getBatches", req, false, extractor, opts);
  };

  public async getFilteredBatches(tenantId: Guid, filter: BatchFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredBatches> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredBatches(response.results);;
    return this.makeJsonRequest("batch/v1/getFilteredBatches", req, false, extractor, opts);
  };

  public async generateBatchControlSheet(tenantId: Guid, documentFormat: EnumDocumentFileType, documentTemplateId: Guid | null, batchId: Guid, sectionsControl: SectionsControl, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Uint8Array> {
    const req = {
    tenantId:tenantId.toString(),
    documentFormat:documentFormat.value,
    documentTemplateId:documentTemplateId == null ? null : documentTemplateId.toString(),
    batchId:batchId.toString(),
    sectionsControl:sectionsControl.toJsonSerializedObject(),
    };
    const extractor = (response:any) => converters.base64ToUint8Array(response.documentBytes);;
    return this.makeJsonRequest("batch/v1/generateBatchControlSheet", req, false, extractor, opts);
  };

  public async migrateAssembliesToMegaBatches(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/migrateAssembliesToMegaBatches", req, false, extractor, opts);
  };

  public async getBatchCostSummary(tenantId: Guid, batchId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ProcessCostSummary[]> {
    const req = {
    tenantId:tenantId.toString(),
    batchId:batchId.toString(),
    };
    const extractor = (response:any) => response.batchCostSummary.map((v: any) => new ProcessCostSummary(v));;
    return this.makeJsonRequest("batch/v1/getBatchCostSummary", req, false, extractor, opts);
  };

  public async evaluateLiquidProduction(tenantId: Guid, fromDate: LocalDate, toDate: LocalDate, alcoholTypes: readonly EnumAlcoholType[], stockLocations: readonly Guid[] | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<BatchLiquidEvaluation[]> {
    const req = {
    tenantId:tenantId.toString(),
    fromDate:fromDate.toString(),
    toDate:toDate.toString(),
    alcoholTypes:alcoholTypes.map(v => v.value),
    stockLocations:stockLocations == null ? null : stockLocations.map(v => v.toString()),
    };
    const extractor = (response:any) => response.results.map((v: any) => new BatchLiquidEvaluation(v));;
    return this.makeJsonRequest("batch/v1/evaluateLiquidProduction", req, false, extractor, opts);
  };

  public async getBatchHeader(tenantId: Guid, batchId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<BatchHeader> {
    const req = {
    tenantId:tenantId.toString(),
    batchId:batchId.toString(),
    };
    const extractor = (response:any) => new BatchHeader(response.batchHeader);;
    return this.makeJsonRequest("batch/v1/getBatchHeader", req, false, extractor, opts);
  };

  public async getBatchHeaders(tenantId: Guid, batchIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<BatchHeader[]> {
    const req = {
    tenantId:tenantId.toString(),
    batchIds:batchIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.batchHeaders.map((v: any) => new BatchHeader(v));;
    return this.makeJsonRequest("batch/v1/getBatchHeaders", req, false, extractor, opts);
  };

  public async getFilteredBatchHeaders(tenantId: Guid, filter: BatchFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredBatchHeaders> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredBatchHeaders(response.results);;
    return this.makeJsonRequest("batch/v1/getFilteredBatchHeaders", req, false, extractor, opts);
  };

  public async setBatchIsHidden(tenantId: Guid, batchId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    batchId:batchId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/setBatchIsHidden", req, true, extractor, opts);
  };

  public async saveBatchHeader(tenantId: Guid, header: BatchHeader, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    header:header.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/saveBatchHeader", req, true, extractor, opts);
  };

  public async cancelBatch(tenantId: Guid, batchId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    batchId:batchId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/cancelBatch", req, true, extractor, opts);
  };

  public async completeBatch(tenantId: Guid, batchId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    batchId:batchId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/completeBatch", req, true, extractor, opts);
  };

  public async uncompleteBatch(tenantId: Guid, batchId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    batchId:batchId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/uncompleteBatch", req, true, extractor, opts);
  };

  public async startBatch(tenantId: Guid, batchId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    batchId:batchId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/startBatch", req, true, extractor, opts);
  };

  public async getAllBatchHeaders(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<BatchHeader[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.batchHeaders.map((v: any) => new BatchHeader(v));;
    return this.makeJsonRequest("batch/v1/getAllBatchHeaders", req, false, extractor, opts);
  };

  public async getProcess(tenantId: Guid, processId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Process> {
    const req = {
    tenantId:tenantId.toString(),
    processId:processId.toString(),
    };
    const extractor = (response:any) => new Process(response.process);;
    return this.makeJsonRequest("batch/v1/getProcess", req, false, extractor, opts);
  };

  public async getProcesses(tenantId: Guid, processIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Process[]> {
    const req = {
    tenantId:tenantId.toString(),
    processIds:processIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.processes.map((v: any) => new Process(v));;
    return this.makeJsonRequest("batch/v1/getProcesses", req, false, extractor, opts);
  };

  public async getFilteredProcesses(tenantId: Guid, filter: ProcessFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredProcesses> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredProcesses(response.results);;
    return this.makeJsonRequest("batch/v1/getFilteredProcesses", req, false, extractor, opts);
  };

  public async generateProcessControlSheet(tenantId: Guid, documentFormat: EnumDocumentFileType, documentTemplateId: Guid | null, processId: Guid, sectionsControl: SectionsControl, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Uint8Array> {
    const req = {
    tenantId:tenantId.toString(),
    documentFormat:documentFormat.value,
    documentTemplateId:documentTemplateId == null ? null : documentTemplateId.toString(),
    processId:processId.toString(),
    sectionsControl:sectionsControl.toJsonSerializedObject(),
    };
    const extractor = (response:any) => converters.base64ToUint8Array(response.documentBytes);;
    return this.makeJsonRequest("batch/v1/generateProcessControlSheet", req, false, extractor, opts);
  };

  public async getProcessStockExpectations(tenantId: Guid, limitStockLocationIds: readonly Guid[] | null, limitStockItemIds: readonly Guid[] | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ProcessStockExpectationLine[]> {
    const req = {
    tenantId:tenantId.toString(),
    limitStockLocationIds:limitStockLocationIds == null ? null : limitStockLocationIds.map(v => v.toString()),
    limitStockItemIds:limitStockItemIds == null ? null : limitStockItemIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.results.map((v: any) => new ProcessStockExpectationLine(v));;
    return this.makeJsonRequest("batch/v1/getProcessStockExpectations", req, false, extractor, opts);
  };

  public async getAllBlockingQcTests(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ProcessBlockingQcTest[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.processBlockingQcTests.map((v: any) => new ProcessBlockingQcTest(v));;
    return this.makeJsonRequest("batch/v1/getAllBlockingQcTests", req, false, extractor, opts);
  };

  public async getBlockingQcTestsForProcesses(tenantId: Guid, processIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ProcessBlockingQcTest[]> {
    const req = {
    tenantId:tenantId.toString(),
    processIds:processIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.processBlockingQcTests.map((v: any) => new ProcessBlockingQcTest(v));;
    return this.makeJsonRequest("batch/v1/getBlockingQcTestsForProcesses", req, false, extractor, opts);
  };

  public async getProcessHeader(tenantId: Guid, processId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ProcessHeader> {
    const req = {
    tenantId:tenantId.toString(),
    processId:processId.toString(),
    };
    const extractor = (response:any) => new ProcessHeader(response.processHeader);;
    return this.makeJsonRequest("batch/v1/getProcessHeader", req, false, extractor, opts);
  };

  public async getProcessHeaders(tenantId: Guid, processIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ProcessHeader[]> {
    const req = {
    tenantId:tenantId.toString(),
    processIds:processIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.processHeaders.map((v: any) => new ProcessHeader(v));;
    return this.makeJsonRequest("batch/v1/getProcessHeaders", req, false, extractor, opts);
  };

  public async getFilteredProcessHeaders(tenantId: Guid, filter: ProcessFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredProcessHeaders> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredProcessHeaders(response.results);;
    return this.makeJsonRequest("batch/v1/getFilteredProcessHeaders", req, false, extractor, opts);
  };

  public async saveProcessHeader(tenantId: Guid, header: ProcessHeader, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    header:header.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/saveProcessHeader", req, true, extractor, opts);
  };

  public async startProcess(tenantId: Guid, processId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    processId:processId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/startProcess", req, true, extractor, opts);
  };

  public async completeProcess(tenantId: Guid, processId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    processId:processId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/completeProcess", req, true, extractor, opts);
  };

  public async uncompleteProcess(tenantId: Guid, processId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    processId:processId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/uncompleteProcess", req, true, extractor, opts);
  };

  public async hardDeleteProcess(tenantId: Guid, processId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    processId:processId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/hardDeleteProcess", req, true, extractor, opts);
  };

  public async savePrintUpdatesForProcess(tenantId: Guid, processId: Guid, hasPrintedLabels: boolean, bestBeforeDate: LocalDate | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    processId:processId.toString(),
    hasPrintedLabels:hasPrintedLabels,
    bestBeforeDate:bestBeforeDate == null ? null : bestBeforeDate.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/savePrintUpdatesForProcess", req, true, extractor, opts);
  };

  public async startProcesses(tenantId: Guid, processIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    processIds:processIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/startProcesses", req, true, extractor, opts);
  };

  public async saveProcessInstruction(tenantId: Guid, processInstruction: ProcessInstruction, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    processInstruction:processInstruction.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/saveProcessInstruction", req, true, extractor, opts);
  };

  public async saveProcessInstructions(tenantId: Guid, processInstructions: readonly ProcessInstruction[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    processInstructions:processInstructions.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/saveProcessInstructions", req, true, extractor, opts);
  };

  public async hardDeleteProcessInstruction(tenantId: Guid, processInstructionId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    processInstructionId:processInstructionId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/hardDeleteProcessInstruction", req, true, extractor, opts);
  };

  public async getProcessInstructions(tenantId: Guid, processInstructionIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ProcessInstruction[]> {
    const req = {
    tenantId:tenantId.toString(),
    processInstructionIds:processInstructionIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.processInstructions.map((v: any) => new ProcessInstruction(v));;
    return this.makeJsonRequest("batch/v1/getProcessInstructions", req, false, extractor, opts);
  };

  public async getAllProcessInstructions(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ProcessInstruction[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.processInstructions.map((v: any) => new ProcessInstruction(v));;
    return this.makeJsonRequest("batch/v1/getAllProcessInstructions", req, false, extractor, opts);
  };

  public async saveProcessIngredientLine(tenantId: Guid, line: ProcessIngredientLine, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    line:line.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/saveProcessIngredientLine", req, true, extractor, opts);
  };

  public async saveProcessIngredientLines(tenantId: Guid, lines: readonly ProcessIngredientLine[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    lines:lines.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/saveProcessIngredientLines", req, true, extractor, opts);
  };

  public async hardDeleteProcessIngredientLines(tenantId: Guid, lineIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    lineIds:lineIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/hardDeleteProcessIngredientLines", req, true, extractor, opts);
  };

  public async useIngredientsFromStock(tenantId: Guid, usedIngredients: readonly ProcessIngredientUsedLine[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    usedIngredients:usedIngredients.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/useIngredientsFromStock", req, true, extractor, opts);
  };

  public async saveProcessUsedIngredientLines(tenantId: Guid, lines: readonly ProcessIngredientUsedLine[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    lines:lines.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/saveProcessUsedIngredientLines", req, true, extractor, opts);
  };

  public async getRecommendedBatchesForProcess(tenantId: Guid, processId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<IngredientRecommendedBatch[]> {
    const req = {
    tenantId:tenantId.toString(),
    processId:processId.toString(),
    };
    const extractor = (response:any) => response.recommendedBatches.map((v: any) => new IngredientRecommendedBatch(v));;
    return this.makeJsonRequest("batch/v1/getRecommendedBatchesForProcess", req, false, extractor, opts);
  };

  public async getRecommendedBatchesForPickingList(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<IngredientRecommendedBatch[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.recommendedBatches.map((v: any) => new IngredientRecommendedBatch(v));;
    return this.makeJsonRequest("batch/v1/getRecommendedBatchesForPickingList", req, false, extractor, opts);
  };

  public async getProcessIngredientsByProcessIds(tenantId: Guid, processIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ProcessIngredient[]> {
    const req = {
    tenantId:tenantId.toString(),
    processIds:processIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.processIngredients.map((v: any) => new ProcessIngredient(v));;
    return this.makeJsonRequest("batch/v1/getProcessIngredientsByProcessIds", req, false, extractor, opts);
  };

  public async produceItemsToStock(tenantId: Guid, processId: Guid, producedItems: readonly ProcessProducedItem[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    processId:processId.toString(),
    producedItems:producedItems.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/produceItemsToStock", req, true, extractor, opts);
  };

  public async produceItemToProcess(tenantId: Guid, sourceProcessId: Guid, targetProcessId: Guid, targetType: EnumProcessOutputTarget, producedItem: ProcessProducedItem, targetIngredientLineId: Guid, urn: string, sourceRemainingExpectedQuantity: number, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    sourceProcessId:sourceProcessId.toString(),
    targetProcessId:targetProcessId.toString(),
    targetType:targetType.value,
    producedItem:producedItem.toJsonSerializedObject(),
    targetIngredientLineId:targetIngredientLineId.toString(),
    urn:urn,
    sourceRemainingExpectedQuantity:sourceRemainingExpectedQuantity,
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/produceItemToProcess", req, true, extractor, opts);
  };

  public async getProcessPrerequisitesByProcessIds(tenantId: Guid, processIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ProcessPrerequisite[]> {
    const req = {
    tenantId:tenantId.toString(),
    processIds:processIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.prerequisites.map((v: any) => new ProcessPrerequisite(v));;
    return this.makeJsonRequest("batch/v1/getProcessPrerequisitesByProcessIds", req, false, extractor, opts);
  };

  public async updatePrerequisiteForProcess(tenantId: Guid, processHeaderId: Guid, prerequisiteProcessIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    processHeaderId:processHeaderId.toString(),
    prerequisiteProcessIds:prerequisiteProcessIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/updatePrerequisiteForProcess", req, true, extractor, opts);
  };

  public async saveProcessVesselRequirement(tenantId: Guid, requirement: ProcessVesselRequirement, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    requirement:requirement.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/saveProcessVesselRequirement", req, true, extractor, opts);
  };

  public async hardDeleteProcessVesselRequirement(tenantId: Guid, requirementId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    requirementId:requirementId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("batch/v1/hardDeleteProcessVesselRequirement", req, true, extractor, opts);
  };
}

export const batchService = new BatchService();